/**
 * La región del usuario: con qué idioma, formatos y zona se muestran los números y las fechas en
 * `fsd/`. Se lee con `useRegion()`; nunca con `i18n.locale` ni con la zona del navegador.
 *
 * Resuelve igual que `useFormatters` del código existente (paridad estricta):
 * `timeZone` y `hour12` pueden quedar sin definir y en ese caso `Intl` usa la zona del navegador y lo
 * que diga el locale.
 */
export interface Region {
  /** El idioma de la interfaz (`es` | `en`), el que tiene activo Lingui. */
  locale: string;
  /** Locale BCP 47 para números (`es-CL`, `en-US`…); sin preferencia, el idioma de la interfaz. */
  numberLocale: string;
  /** Locale BCP 47 para fechas (`es-CL`, `en-US`…); sin preferencia, el idioma de la interfaz. */
  dateLocale: string;
  /** Zona IANA (`America/Santiago`…). `undefined` = la zona del navegador. */
  timeZone?: string;
  /** Reloj de 12 h. `undefined` = lo que dé el locale (`en-US` usa 12 h, `es` 24 h). */
  hour12?: boolean;
  /**
   * Cómo se muestra el mes en las fechas (`short`, `long`, `numeric`…), el `month` de
   * `Intl.DateTimeFormat`. Es la preferencia `dateMonthFormat` del usuario; sin región, `short`.
   */
  dateMonthFormat: Intl.DateTimeFormatOptions['month'];
}

/**
 * La región sin preferencias del usuario ni de la organización: lo mismo que hace `useFormatters`
 * mientras no llegaron (idioma `es`, zona de Santiago, `hour12` del locale, mes `short`). Es la única
 * definición del default: `resolveRegion` (`fsd/app/locale`) toma de acá la zona y el formato de mes
 * cuando el usuario no tiene región.
 */
export const DEFAULT_REGION: Region = {
  locale: 'es',
  numberLocale: 'es',
  dateLocale: 'es',
  timeZone: 'America/Santiago',
  hour12: undefined,
  dateMonthFormat: 'short',
};
